import { ICarrierApplication } from 'types'
import { STEPS, TStep } from '../components/activation/Steps'
import { ICarrierApplicationQuery } from '../components/activation/graphql/CarrierApplication'
import { IMotorCarrierInsuranceContactsQuery } from '../pages/carrier-profile/components/InsuranceContactInformationCard/graphql/InsuranceContactsQuery'

// Carrier application prop getter
const getCarrierApplicationProp = <K extends keyof ICarrierApplication>(
  carrierApplication: ICarrierApplication,
  keyName: K,
  defaultValue?: ICarrierApplication[K] | undefined
): ICarrierApplication[K] | undefined => {
  if (!carrierApplication) return defaultValue // undefined, if not set
  return carrierApplication[keyName]
}

interface IActivationSteps {
  hasSignedBca: boolean
  hasAddedReferences: boolean
  hasUploadedDocuments: boolean
}

interface INewActivationSteps {
  hasSignedBca: boolean
  hasInsuranceContacts: boolean
  hasUploadedDocuments: boolean
  needsSafetyLetter?: boolean
}

// Checks carrier application for bca, references and packagefiles to determine if those steps have been completed already.
export const getHasCompletedActivationSteps = (
  carrierApplication: ICarrierApplication
): IActivationSteps => {
  const brokerCarrierAgreement = getCarrierApplicationProp(
    carrierApplication,
    'brokerCarrierAgreement'
  )
  const carrierReferences = getCarrierApplicationProp(carrierApplication, 'carrierReferences', [])
  const packageFiles = getCarrierApplicationProp(carrierApplication, 'packageFiles', [])
  return {
    hasSignedBca: !!brokerCarrierAgreement,
    hasAddedReferences: carrierReferences ? !!carrierReferences.length : false,
    hasUploadedDocuments: packageFiles ? !!packageFiles.length : false,
  }
}

// Checks carrier application for bca, references and packagefiles to determine if those steps have been completed already.
export const getNewHasCompletedActivationSteps = (
  carrierDetails: ICarrierApplication,
  carrierApplicationData: ICarrierApplicationQuery,
  insuranceContactData: IMotorCarrierInsuranceContactsQuery,
  carrierAppMilestoneFiveCarrierUpload: boolean
) => {
  // check if carrier signed the broker carrier agreement
  const { carrierApplication } = carrierApplicationData || {}
  const brokerCarrierAgreement = getCarrierApplicationProp(carrierDetails, 'brokerCarrierAgreement')
  const { brokerCarrierAgreement: bcaAgreement, hasInsurance, hasW9, safetyLetterNeeded } =
    carrierApplication || {}

  const { id: brokerCarrierAgreementId } = bcaAgreement || {}
  // check if carrier has Insurance Contacts
  const { insuranceContacts = [] } = insuranceContactData || {}
  const packageFiles = getCarrierApplicationProp(carrierDetails, 'packageFiles', [])

  const hasCarrierUploadedW9 = carrierAppMilestoneFiveCarrierUpload
    ? hasW9 || false
    : !!packageFiles?.length || hasW9 || false

  const hasCarrierInsurance = carrierAppMilestoneFiveCarrierUpload
    ? hasInsurance || false
    : !!insuranceContacts?.length || hasInsurance || false

  const hasCarrierSignedBca = carrierAppMilestoneFiveCarrierUpload
    ? !!brokerCarrierAgreementId
    : !!brokerCarrierAgreementId || !!brokerCarrierAgreement || false

  return {
    hasSignedBca: hasCarrierSignedBca,
    hasInsuranceContacts: hasCarrierInsurance,
    hasUploadedDocuments: hasCarrierUploadedW9,
    needsSafetyLetter: safetyLetterNeeded,
  }
}

// Checks completed steps to determine the current step the dispatcher is on for the Activation flow.
export const getCurrentActivationStep = (hasCompletedActivationSteps: IActivationSteps): TStep => {
  const { hasSignedBca, hasUploadedDocuments } = hasCompletedActivationSteps || {}
  if (hasSignedBca === false) {
    return 'bca'
  }
  if (hasUploadedDocuments === false) {
    return 'documents'
  }
  return 'bca'
}

type TNewStep = keyof typeof STEPS

export const getNewCurrentActivationStep = (
  hasCompletedActivationSteps: INewActivationSteps
): TNewStep => {
  const { hasInsuranceContacts, hasSignedBca, hasUploadedDocuments, needsSafetyLetter } =
    hasCompletedActivationSteps || {}
  if (hasSignedBca === false) {
    return 'bca'
  }
  if (hasInsuranceContacts === false) {
    return 'insurance'
  }
  if (hasUploadedDocuments === false) {
    return 'documents'
  }
  if (needsSafetyLetter) {
    return 'safetyLetter'
  }
  return 'bca'
}
